import { Link, useLocation } from 'react-router';
import { BookmarkFilledIcon, BookmarkOutlineIcon } from 'assets/images/sidebar';
import { Typography } from '@components/Typography';
import ActionIconButton from '@components/ActionIconButton';
import { useTranslation } from '@hooks';
import { UpgradeIcon } from 'assets/images/icons';
import { BookmarkButton, ListItem, PopoverItem, buttonReset } from './styles';

export const isBookmarkable = (item, toggleBookmark) => !!item.to && !!toggleBookmark;

export const isNavigationItemActive = (item, pathname) => {
    if (item.children?.length) {
        return item.base
            ? [].concat(item.base).some(base => pathname.startsWith(base))
            : item.children.some(child => isNavigationItemActive(child, pathname));
    }
    return item.to === '/' ? pathname === '/' : !!item.to && pathname.startsWith(item.base || item.to);
};

const NavigationLink = ({
    item,
    callback,
    disabled,
    compact,
    nested,
    bookmarks,
    toggleBookmark,
    saving,
    activeBranch = true,
}) => {
    const { pathname } = useLocation();
    const { t } = useTranslation();
    const bookmarked = bookmarks?.includes(item.to) || false;
    const isActive = activeBranch && isNavigationItemActive(item, pathname);
    const canBookmark = isBookmarkable(item, toggleBookmark);
    const Row = compact ? PopoverItem : ListItem;
    const ItemLink = item.external ? 'a' : item.to ? Link : 'button';
    const isButton = ItemLink === 'button';

    return (
        <Row
            {...(!compact && { component: 'div' })}
            isActive={isActive}
            type="single"
            className="sidebar-navigation-row"
            sx={{ padding: 0, position: 'relative' }}
        >
            {!compact && <span className="sidebar-active-indicator" />}
            <ItemLink
                to={item.external || isButton ? undefined : item.to}
                type={isButton ? 'button' : undefined}
                href={item.external ? item.href : undefined}
                target={item.external ? '_blank' : undefined}
                rel={item.external ? 'noopener' : undefined}
                id={item.id}
                aria-disabled={disabled || undefined}
                aria-current={isActive ? 'page' : undefined}
                onClick={event => {
                    if (disabled) event.preventDefault();
                    else callback?.(item);
                }}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    flex: 1,
                    minWidth: 0,
                    textDecoration: 'none',
                    padding: compact ? '4px 16px' : '8px 16px 8px 24px',
                    paddingRight: canBookmark ? 44 : 16,
                    ...(isButton && buttonReset),
                }}
            >
                {!compact && !nested && item.icon}
                <Typography variant={compact || nested ? 'body2' : 'body1'} sx={{ overflowWrap: 'anywhere' }}>
                    {item.title}
                </Typography>
                {item.badge}
                {item.upgradeRequired && <UpgradeIcon style={{ marginLeft: 'auto', flexShrink: 0 }} />}
            </ItemLink>
            {canBookmark && (
                <ActionIconButton
                    component={BookmarkButton}
                    title={t(bookmarked ? 'Sidebar.removeBookmark' : 'Sidebar.addBookmark')}
                    className="sidebar-bookmark"
                    type="button"
                    aria-pressed={bookmarked}
                    loading={saving}
                    onClick={() => toggleBookmark(item.to)}
                    size="small"
                    disableRipple
                >
                    <BookmarkFilledIcon className="sidebar-bookmark-icon sidebar-bookmark-filled" aria-hidden="true" />
                    <BookmarkOutlineIcon
                        className="sidebar-bookmark-icon sidebar-bookmark-outline"
                        aria-hidden="true"
                    />
                </ActionIconButton>
            )}
        </Row>
    );
};

export default NavigationLink;
